/**
 * useHealthKit
 *
 * Reads Apple HealthKit (iOS) or Google Health Connect (Android) data and
 * uploads daily metric snapshots to the `store-health-snapshot` Edge Function.
 *
 * On the web platform this hook is a no-op — all exports are safe to call
 * unconditionally.
 *
 * Uses `@perfood/capacitor-healthkit` for iOS and degrades gracefully on
 * Android (Health Connect support TBD).
 *
 * Activation: call `requestPermissionsAndSync()` once the user has opted in
 * via the mobile onboarding flow or the Health tab in profile settings.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

/** Detects whether the app is running inside a Capacitor native shell. */
function isNative(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof (
      window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }
    ).Capacitor !== "undefined" &&
    (
      window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }
    ).Capacitor?.isNativePlatform?.() === true
  );
}

function getPlatform(): string {
  if (!isNative()) return "web";
  return (
    (
      window as Window & { Capacitor?: { getPlatform?: () => string } }
    ).Capacitor?.getPlatform?.() ?? "web"
  );
}

async function loadCapacitorHealthkit() {
  const moduleName = "@perfood/" + "capacitor-healthkit";
  return import(/* @vite-ignore */ moduleName);
}

async function loadCapacitorApp() {
  const moduleName = "@capacitor/" + "app";
  return import(/* @vite-ignore */ moduleName);
}

/** Minimum gap between automatic background syncs (12 hours). */
const AUTO_SYNC_INTERVAL_MS = 12 * 60 * 60 * 1000;

export type HealthMetricType =
  | "steps"
  | "heart_rate"
  | "sleep_duration"
  | "active_energy"
  | "resting_heart_rate"
  | "heart_rate_variability"
  | "blood_oxygen"
  | "respiratory_rate";

export interface HealthSnapshot {
  metricType: HealthMetricType;
  value: number;
  unit: string;
  sampleDate: string; // YYYY-MM-DD
  source?: string;
}

type SyncStatus = "idle" | "requesting" | "syncing" | "done" | "error";

export function useHealthKit() {
  const [status, setStatus] = useState<SyncStatus>("idle");
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [storedCount, setStoredCount] = useState(0);
  // Track whether an app-resume listener has been attached to avoid duplicates.
  const appListenerAttached = useRef(false);

  /**
   * Collects the last `days` days of health metrics from HealthKit/
   * Health Connect and uploads them to the backend.
   */
  const requestPermissionsAndSync = useCallback(async (days = 30) => {
    if (!isNative()) {
      logger.info("useHealthKit: skipping — not running on native");
      return;
    }

    const platform = getPlatform();

    if (platform !== "ios") {
      logger.info("useHealthKit: Android Health Connect not yet implemented");
      return;
    }

    setStatus("requesting");
    setSyncError(null);

    try {
      const { CapacitorHealthkit } = await loadCapacitorHealthkit();

      const readTypes = [
        "steps",
        "heart_rate",
        "sleep_analysis",
        "active_energy_burned",
        "resting_heart_rate",
        "heart_rate_variability_sdnn",
        "oxygen_saturation",
        "respiratory_rate",
      ];

      // Request HealthKit read permissions
      await CapacitorHealthkit.requestAuthorization({
        all: [],
        read: readTypes,
        write: [],
      });

      setStatus("syncing");

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const snapshots: HealthSnapshot[] = [];

      /**
       * Queries a single HealthKit sample type and appends matching
       * snapshots to the accumulator array. Errors in individual metric
       * queries are logged and swallowed so that one unavailable metric
       * does not abort the entire sync.
       */
      async function queryMetric(
        sampleName: string,
        metricType: HealthMetricType,
        unit: string
      ) {
        try {
          const result = await CapacitorHealthkit.queryHKitSampleType({
            sampleName,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            limit: 0,
          });
          for (const s of result.resultData ?? []) {
            if (s.startDate && s.value !== null && s.value !== undefined) {
              snapshots.push({
                metricType,
                value: Number(s.value),
                unit,
                sampleDate: s.startDate.split("T")[0],
                source: s.sourceName,
              });
            }
          }
        } catch (e) {
          logger.warn(`HealthKit ${metricType} query failed:`, e);
        }
      }

      await queryMetric("steps", "steps", "count");
      await queryMetric("heart_rate", "heart_rate", "bpm");
      await queryMetric("sleep_analysis", "sleep_duration", "min");
      await queryMetric("active_energy_burned", "active_energy", "kcal");
      await queryMetric("resting_heart_rate", "resting_heart_rate", "bpm");
      await queryMetric(
        "heart_rate_variability_sdnn",
        "heart_rate_variability",
        "ms"
      );
      await queryMetric("oxygen_saturation", "blood_oxygen", "%");
      await queryMetric("respiratory_rate", "respiratory_rate", "brpm");

      // ── Upload batched snapshots ────────────────────────────────────────
      if (snapshots.length > 0) {
        const { error } = await supabase.functions.invoke(
          "store-health-snapshot",
          { body: { snapshots } }
        );
        if (error) throw error;
      }

      setStoredCount(snapshots.length);
      setLastSyncedAt(new Date());
      setStatus("done");
    } catch (err) {
      logger.error("useHealthKit sync failed:", err);
      const message =
        err instanceof Error
          ? err.message
          : typeof err === "object" &&
              err !== null &&
              "message" in err &&
              typeof err.message === "string"
            ? err.message
            : String(err);
      setSyncError(message);
      setStatus("error");
    }
  }, []);

  /**
   * Auto-sync on app resume (native iOS only).
   *
   * Listens for the Capacitor `appStateChange` event.  When the app comes
   * back to the foreground and at least AUTO_SYNC_INTERVAL_MS has passed
   * since the last sync, we silently pull the most recent 30 days.
   */
  useEffect(() => {
    if (!isNative() || getPlatform() !== "ios") return;
    if (appListenerAttached.current) return;
    appListenerAttached.current = true;

    let handle: { remove: () => void } | null = null;

    loadCapacitorApp()
      .then(({ App: CapApp }) => {
        handle = CapApp.addListener(
          "appStateChange",
          (state: { isActive: boolean }) => {
            if (!state.isActive) return;
            const now = Date.now();
            const elapsed = lastSyncedAt
              ? now - lastSyncedAt.getTime()
              : Infinity;
            if (elapsed >= AUTO_SYNC_INTERVAL_MS) {
              void requestPermissionsAndSync(30);
            }
          }
        );
      })
      .catch((e) => {
        logger.warn(
          "useHealthKit: could not attach appStateChange listener",
          e
        );
      });

    return () => {
      handle?.remove();
    };
  }, [lastSyncedAt, requestPermissionsAndSync]);

  return {
    status,
    lastSyncedAt,
    syncError,
    storedCount,
    requestPermissionsAndSync,
    isSupported: isNative() && getPlatform() === "ios",
  };
}
